<script setup lang="ts">
import { computed, ref } from 'vue';
import { useToast } from 'vue-toastification';
import { onBeforeRouteUpdate } from 'vue-router';
import {
	err, errAsync, ok, okAsync,
} from 'neverthrow';
import { useI18n } from 'vue-i18n';
import { match } from 'ts-pattern';
import IconPlus from '../assets/icon-plus.svg?component';
import { Project } from '@/models/Project';
import { Client } from '@/models/Client';
import {
	Quota,
	startsAfterSpecificDate,
	isActiveDuringSpecificDate,
	QuotaSubmitData,
	startsBeforeSpecificDate,
	getRemainingQuotaHoursForSpecificDate,
	getActiveQuotaDuringTimeRange,
	removeQuotaFromArray, validateQuota, QuotaValidationOptions,
} from '@/models/Quota';
import { useQuotasQuery } from '@/composables/queries/useQuotasQuery';
import { useQuotaInfoQuery } from '@/composables/queries/useQuotaInfoQuery';
import { useProjectsQuotasQuery } from '@/composables/queries/useProjectsQuotasQuery';
import IconClock from '@/assets/clock-icon.svg?component';
import IconExtraQuota from '@/assets/layer-plus-solid.svg?component';
import { useToday } from '@/composables/useToday';
import { hoursToDisplayTime } from '@/shared/times';
import QuotaListItem from '@/components/QuotaListItem.vue';
import QuotaForm from '@/components/QuotaForm.vue';
import { useQuotasMutation } from '@/composables/queries/useQuotasMutation';
import { useMainStore } from '@/store/main';
import { getDateTime, getSafeArray, wrapResultAsyncFunction } from '@/utilities/Helpers';
import { useConfirmationModal } from '@/composables/useConfirmationModal';
import { useLock } from '@/composables/useLock';

const props = withDefaults(defineProps<{
	client: Client;
	project?: Project | null;
}>(), {
	project: null,
});

const { t } = useI18n();
const isCreateModeActive = ref(false);

const today = useToday();

const { data: projectQuotasData } = useQuotasQuery(computed(() => ({
	quotaInfoId: props.project?.quotaInfoId,
})));

const projectQuotas = computed(() => getSafeArray(projectQuotasData.value));

const { data: clientQuotasData } = useQuotasQuery(computed(() => ({
	quotaInfoId: props.client.quotaInfoId,
})));

const clientQuotas = computed(() => getSafeArray(clientQuotasData.value));

const { data: clientProjectsQuotasData, refetch: refetchClientProjectsQuotasData } = useProjectsQuotasQuery(computed(() => ({
	clientId: props.client.id,
})));

const clientProjectsQuotas = computed(() => getSafeArray(clientProjectsQuotasData.value));

const { data: quotaInfo } = useQuotaInfoQuery(computed(() => ({
	quotaInfoId: props.project?.quotaInfoId,
})));

const quotasForSelectedEntity = computed<Quota[]>(() => {
	if (props.project) {
		return projectQuotas.value;
	}

	return clientQuotas.value;
});

const activeClientQuota = computed(() => getSafeArray(clientQuotas.value)
	.find((quota) => isActiveDuringSpecificDate(today.value, quota)));

const futureAndActiveQuotas = computed<Quota[]>(() => quotasForSelectedEntity.value
	.filter((quota) => startsAfterSpecificDate(today.value, quota) || isActiveDuringSpecificDate(today.value, quota)));

const pastQuotas = computed<Quota[]>(() => quotasForSelectedEntity.value
	.filter((quota) => startsBeforeSpecificDate(today.value, quota) && !isActiveDuringSpecificDate(today.value, quota)));

const activeQuota = computed(() => quotasForSelectedEntity.value
	.find((quota) => isActiveDuringSpecificDate(today.value, quota)));

const quotaMonthlyHours = computed(() => {
	if (quotaInfo.value?.isExtra) {
		return activeQuota.value?.monthlyHours ?? null;
	}

	if (!activeClientQuota.value?.monthlyHours) {
		return null;
	}

	return activeClientQuota.value.monthlyHours;
});

const remainingQuotaMonthlyHours = computed(() => {
	if (quotaInfo.value?.isExtra) {
		return quotaMonthlyHours.value;
	}

	return getRemainingQuotaHoursForSpecificDate(clientQuotas.value, clientProjectsQuotas.value, today.value);
});

const createQuotaErrorMessage = ref('');

const {
	createQuota,
	updateQuota: updateQuotaMutation,
	deleteQuota,
} = useQuotasMutation();

const mainStore = useMainStore();

const toast = useToast();

const {
	lock: lockInteraction,
	unlock: unlockInteraction,
	isLocked: isInteractionLocked,
} = useLock();

const startCreateMode = () => {
	createQuotaErrorMessage.value = '';
	isCreateModeActive.value = true;
	lockInteraction('createMode');
};

const onCancelCreateMode = () => {
	createQuotaErrorMessage.value = '';
	isCreateModeActive.value = false;
	unlockInteraction();
};

onBeforeRouteUpdate(onCancelCreateMode);

const onStartEditMode = () => {
	lockInteraction('editMode');
};

const onEndEditMode = () => {
	unlockInteraction();
};

const { show: showOverwriteQuotaConfirmationModal } = useConfirmationModal({
	title: t('modal.conflictingQuota.title'),
	description: t('modal.conflictingQuota.description'),
	cancelLabel: t('common.cancel'),
	confirmLabel: t('common.confirm'),
});

const computedMessages = computed(() => ({
	onlyOneActiveQuotaAllowed: t('error.onlyOneActiveQuotaAllowed'),
	endDateMustBeLessThan: t('error.endDateMustBeLessThan'),
	quotaCannotUpdatedToThisDate: t('error.quotaCannotUpdatedToThisDate'),
	successCreateQuota: t('notifications.successCreateQuota'),
	successUpdatedQuota: t('notifications.successUpdatedQuota'),
	successDeletedQuota: t('notifications.successDeletedQuota'),
	projectsConsumeTooMuchQuota: t('admin.projectsConsumeTooMuchQuota'),
	contingentIsNotEnough: t('admin.contingentIsNotEnough'),
	oneProjectInThePeriod: t('admin.oneProjectInThePeriod'),
	hour: t('common.h'),
}));

const validateQuotaMutation = async (newQuota: QuotaSubmitData, options: QuotaValidationOptions) => {
	const validationResult = validateQuota({
		newQuota,
		clientQuotas: clientQuotas.value,
		projectQuotas: clientProjectsQuotasData.value,
	}, options);

	if (validationResult.isErr()) {
		return match(validationResult.error.message)
			.with('activeQuotaDuringTimeRange', () => errAsync(computedMessages.value.onlyOneActiveQuotaAllowed))
			.with('startsAfterSpecificDate', () => errAsync(`${computedMessages.value.endDateMustBeLessThan} ${options.activeQuotaDuringTimeRange?.from.toISODate()}`))
			.with('noAllowOverwrite', () => errAsync(computedMessages.value.quotaCannotUpdatedToThisDate))
			.with('quotaExceedsNewQuotaLimit', () => errAsync(`${computedMessages.value.projectsConsumeTooMuchQuota} (${validationResult.error.exceededHours}${computedMessages.value.hour})`))
			.with('quotaExceedsNewClientQuotaLimit', () => errAsync(`${computedMessages.value.contingentIsNotEnough} (${validationResult.error.exceededHours}${computedMessages.value.hour})`))
			.with('insufficientQuotaCapacity', () => errAsync(`${computedMessages.value.contingentIsNotEnough}  (${validationResult.error.exceededHours}${computedMessages.value.hour})`))
			.with('projectIsInTimeRange', () => errAsync(computedMessages.value.oneProjectInThePeriod))
			.otherwise(() => showOverwriteQuotaConfirmationModal()
				.map(() => true)
				.mapErr(() => computedMessages.value.quotaCannotUpdatedToThisDate));
	}

	return okAsync(true);
};

const onCreateQuota = async (quota: QuotaSubmitData) => {
	if (!mainStore.user) {
		return;
	}

	const newQuota: QuotaSubmitData = { ...quota };

	const isProject = !!props.project;
	newQuota.quotaInfoId = quota.quotaInfoId || props.client?.quotaInfoId || props.project?.quotaInfoId;

	const activeQuotaDuringTimeRange = getActiveQuotaDuringTimeRange(newQuota, quotasForSelectedEntity.value);

	const validationResult = await validateQuotaMutation(newQuota, { activeQuotaDuringTimeRange, allowOverwrite: true, isProject, status: 'create' });

	if (validationResult.isErr()) {
		createQuotaErrorMessage.value = validationResult.error;
		return;
	}

	const clientProjectQuotasWithoutActiveOne = activeQuotaDuringTimeRange
		? removeQuotaFromArray(activeQuotaDuringTimeRange, clientProjectsQuotas.value)
		: clientProjectsQuotas.value;

	const availableQuotaHours = props.project
		? getRemainingQuotaHoursForSpecificDate(clientQuotas.value, clientProjectQuotasWithoutActiveOne, newQuota.from)
		: null;

	if (availableQuotaHours !== null && availableQuotaHours < newQuota.monthlyHours && !newQuota.isExtra) {
		createQuotaErrorMessage.value = t('error.quotaNotAvailable', [`${getDateTime(newQuota.from).toLocaleString()}`, `${availableQuotaHours}`]);
		return;
	}

	if (activeQuotaDuringTimeRange) {
		const updateResult = await updateQuotaMutation.mutateAsync({
			quotaId: activeQuotaDuringTimeRange.quotaId,
			quota: {
				...activeQuotaDuringTimeRange,
				till: newQuota.from.minus({ days: 1 }),
			},
		});

		if (updateResult.isErr()) {
			toast.error(updateResult.error.message);
			return;
		}
	}

	const result = await createQuota.mutateAsync({
		quota: newQuota,
		client: props.client,
		project: props.project ?? undefined,
		user: mainStore.user,
	});

	if (result.isErr()) {
		toast.error(result.error.message);
		return;
	}

	toast.success(`${computedMessages.value.successCreateQuota} "${props.project?.name || props.client.name}"`);

	onCancelCreateMode();
};

const onUpdateQuota = wrapResultAsyncFunction(async (quota: Quota) => {
	const quotasWithoutOneToUpdate = removeQuotaFromArray(quota, quotasForSelectedEntity.value);

	const activeQuotaDuringTimeRange = getActiveQuotaDuringTimeRange(quota, quotasWithoutOneToUpdate);

	const validationResult = await validateQuotaMutation(quota, { activeQuotaDuringTimeRange, allowOverwrite: false, isProject: !!props.project, status: 'update' });

	if (validationResult.isErr()) {
		return err(validationResult.error);
	}

	const mutationResult = await updateQuotaMutation.mutateAsync({ quotaId: quota.quotaId, quota });

	if (mutationResult.isErr()) {
		return err(mutationResult.error.message);
	}

	toast.success(computedMessages.value.successUpdatedQuota);
	onCancelCreateMode();

	refetchClientProjectsQuotasData();
	return ok(mutationResult.value);
});

const onDeleteQuota = (quota: Quota) => wrapResultAsyncFunction(deleteQuota.mutateAsync)({ quota })
	.map((value) => {
		toast.success(computedMessages.value.successDeletedQuota);
		return value;
	});

const isExtraProject = computed(() => projectQuotas.value.some((quota) => quota.isExtra));
</script>

<template>
	<div>
		<div class="text-2xl font-bold mt-10">
			{{ project?.name ?? client?.name }}
		</div>
		<div
			v-if="remainingQuotaMonthlyHours !== null"
			class="flex items-center space-x-2 text-primary-900 mb-2"
		>
			<div class="w-6 h-6 flex items-center justify-center">
				<IconClock class="flex fill-primary-100" />
			</div>
			<div class="text-primary-900">
				{{ $t('admin.availableQuota') }}
			</div>
			<div
				class="text-primary-900"
			>
				<span>
					{{ hoursToDisplayTime(remainingQuotaMonthlyHours) }}{{ $t('common.h') }}
				</span>
				<span v-if="quotaMonthlyHours && remainingQuotaMonthlyHours !== quotaMonthlyHours">
					({{ hoursToDisplayTime(quotaMonthlyHours) }}{{ $t('common.h') }})
				</span>
			</div>
		</div>
		<div v-if="isExtraProject">
			<div class="flex items-center space-x-2">
				<div class="w-6 h-6 flex items-center justify-center">
					<IconExtraQuota class="flex text-success fill-current w-5 h-5 mx-1" />
				</div>
				<span class="text-primary-900 leading-4">{{ $t('admin.projectChargedSeparately') }}</span>
			</div>
		</div>
		<div class="flex justify-between items-center mt-20 mb-3">
			<div class="text-xl">
				{{ $t('common.quota') }}
			</div>
		</div>
		<div class="space-y-3">
			<div
				v-if="futureAndActiveQuotas.length > 0"
				class="grid grid-cols-11 gap-x-4 relative"
			>
				<div class="col-span-3">
					<div class="text-xs text-gray">
						{{ $t('common.startDate') }}
					</div>
				</div>
				<div class="col-span-3">
					<div class="text-xs text-gray">
						{{ $t('common.endDateOptional') }}
					</div>
				</div>
				<div class="col-span-3">
					<div class="text-xs text-gray">
						{{ $t('common.quota') }}
					</div>
				</div>
			</div>
			<QuotaListItem
				v-for="quota in futureAndActiveQuotas"
				:key="quota.quotaId"
				:is-extra="isExtraProject"
				:quota="quota"
				:editable="!isInteractionLocked"
				:on-update="onUpdateQuota"
				:on-delete="onDeleteQuota"
				@start-edit-mode="onStartEditMode"
				@end-edit-mode="onEndEditMode"
			/>
			<QuotaForm
				v-if="isCreateModeActive"
				:is-extra="isExtraProject"
				:active-quotas="quotasForSelectedEntity"
				:show-extra-option="quotasForSelectedEntity.length === 0 && project !== null"
				:error-message="createQuotaErrorMessage"
				@create="onCreateQuota"
				@cancel="onCancelCreateMode"
			/>
			<button
				v-if="!isCreateModeActive"
				type="button"
				class="border-2 border-dashed border-primary-900/30 w-full p-4 rounded flex items-center justify-center space-x-2 text-white/60 hover:text-white/80 hover:border-white/60 disabled:cursor-not-allowed"
				:disabled="isInteractionLocked"
				@click="startCreateMode"
			>
				<IconPlus class="w-4 h-4" />
				<span>{{ $t('admin.addQuota') }}</span>
			</button>
		</div>

		<div class="text-xl mt-8 mb-3">
			{{ $t('admin.quotaHistory') }}
		</div>
		<div
			v-if="pastQuotas.length <= 0"
			class="mb-4 text-gray italic"
		>
			{{ $t('admin.noQuota') }}
		</div>
		<div
			v-else
			class="space-y-3"
		>
			<div class="grid grid-cols-11 gap-x-4 relative">
				<div class="col-span-3">
					<div class="text-xs text-gray">
						{{ $t('common.startDate') }}
					</div>
				</div>
				<div class="col-span-3">
					<div class="text-xs text-gray">
						{{ $t('common.endDateOptional') }}
					</div>
				</div>
				<div class="col-span-3">
					<div class="text-xs text-gray">
						{{ $t('common.quota') }}
					</div>
				</div>
			</div>
			<QuotaListItem
				v-for="quota in pastQuotas"
				:key="quota.quotaId"
				:is-extra="isExtraProject"
				:quota="quota"
			/>
		</div>
	</div>
</template>
